import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getAccountSurfaceFlags } from "../../config/features";
import usePositionsWSS from "../../hooks/wss/usePositionsWSS";
import { MarketPortfolioOptionEnum, MARKET_PORTFOLIO_TAB_SLUGS } from "./types";
import { useUrlTabState } from "../../hooks/useUrlParamState";
import { LAYER_COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { FONT_SIZE } from "../../constants/design/fontSize";
import { MobileOrdersTab } from "../../pages/PortfolioPage/mobile/MobileOrdersTab";
import { MobilePositionsTab } from "../../pages/PortfolioPage/mobile/MobilePositionsTab";
import { MobileTradeHistoryTab } from "../../pages/PortfolioPage/mobile/MobileTradeHistoryTab";
import { OrderHistoryTab } from "../../pages/OrderHistoryTab";
import { PositionHistoryTab } from "../../pages/PositionHistoryTab";
import { ChartTab } from "../Perps/PerpsOrderbookAndChart/style";
import SegmentedControl from "../shared/SegmentedControl";
import {
  MobileTradingBottomBarContainer,
  MobileTradingBottomBarContent,
} from "./style";
import { getVisibleMarketPortfolioTabs } from "./helpers";

const MobileTradingBottomBar = forwardRef<any>((_, ref) => {
  const { t } = useTranslation("app", {
    keyPrefix: "TradingBottomBar.TradingBottomBar",
  });
  const { positions } = usePositionsWSS();
  const { showDerivativeUi } = getAccountSurfaceFlags(positions);
  const visibleTabs = useMemo(
    () =>
      getVisibleMarketPortfolioTabs(showDerivativeUi).filter(
        (tab) => tab !== MarketPortfolioOptionEnum.Portfolio
      ),
    [showDerivativeUi]
  );
  const tabSlugs = useMemo(() => {
    const slugs = {} as Record<MarketPortfolioOptionEnum, string>;
    visibleTabs.forEach((tab) => {
      slugs[tab] = MARKET_PORTFOLIO_TAB_SLUGS[tab];
    });
    return slugs;
  }, [visibleTabs]);
  const defaultTab = visibleTabs.includes(MarketPortfolioOptionEnum.Positions)
    ? MarketPortfolioOptionEnum.Positions
    : MarketPortfolioOptionEnum.OpenOrders;

  // Same `panel` key as the desktop bottom bar deliberately: it is the same tab
  // set, so a link like `?panel=trade-history` resolves identically on either
  // layout and survives a viewport change.
  const [portfolioTab, setPortfolioTab] =
    useUrlTabState<MarketPortfolioOptionEnum>(
      "panel",
      defaultTab,
      tabSlugs
    );
  const activeTab = visibleTabs.includes(portfolioTab)
    ? portfolioTab
    : defaultTab;

  const getTabTitle = useCallback(
    (tab: MarketPortfolioOptionEnum) => {
      switch (tab) {
        case MarketPortfolioOptionEnum.Positions:
          return `${t("positions")}`;
        case MarketPortfolioOptionEnum.OpenOrders:
          return `${t("open_orders")}`;
        case MarketPortfolioOptionEnum.PositionHistory:
          return t("position_history", { defaultValue: "Position History" });
        case MarketPortfolioOptionEnum.TradeHistory:
          return t("trade_history", { defaultValue: "Trade History" });
        case MarketPortfolioOptionEnum.OrderHistory:
          return t("order_history", { defaultValue: "Order History" });
        case MarketPortfolioOptionEnum.Portfolio:
          return "";
        default: {
          const _exhaustive: never = tab;
          return _exhaustive;
        }
      }
    },
    [t]
  );

  const portfolioControls = useMemo(
    () =>
      visibleTabs.map((tab) => ({
        display: <ChartTab>{getTabTitle(tab)}</ChartTab>,
        value: tab,
        textColor:
          tab === activeTab ? TEXT_COLORS.one : TEXT_COLORS.three,
      })),
    [activeTab, getTabTitle, visibleTabs]
  );

  return (
    <MobileTradingBottomBarContainer ref={ref}>
      <SegmentedControl
        segments={portfolioControls}
        value={activeTab}
        onSelect={(e) => setPortfolioTab(e as MarketPortfolioOptionEnum)}
        config={{
          widthType: "fullWidth",
          backgroundColor: LAYER_COLORS.two,
          activeBackgroundColor: LAYER_COLORS.three,
          borderRadius: "10px",
          button: {
            fontSize: FONT_SIZE.one,
          },
        }}
      />
      <MobileTradingBottomBarContent>
        {showDerivativeUi ? (
          <div hidden={activeTab !== MarketPortfolioOptionEnum.Positions}>
            <MobilePositionsTab showFilter={false} noPadding />
          </div>
        ) : null}
        <div hidden={activeTab !== MarketPortfolioOptionEnum.OpenOrders}>
          <MobileOrdersTab showFilter={false} noPadding />
        </div>
        {showDerivativeUi ? (
          <div hidden={activeTab !== MarketPortfolioOptionEnum.PositionHistory}>
            <PositionHistoryTab mode="trading" hideTitle hideFilter />
          </div>
        ) : null}
        <div hidden={activeTab !== MarketPortfolioOptionEnum.TradeHistory}>
          <MobileTradeHistoryTab showFilter={false} noPadding />
        </div>
        <div hidden={activeTab !== MarketPortfolioOptionEnum.OrderHistory}>
          <OrderHistoryTab mode="trading" hideTitle hideFilter />
        </div>
      </MobileTradingBottomBarContent>
    </MobileTradingBottomBarContainer>
  );
});

export default MobileTradingBottomBar;
